import { BASEROW_CONFIG, getBaserowHeaders } from './config';
import { transformBaserowRow, transformToBaserowRow } from './transform';
import { buildMCQFilter, buildPaginationParams } from './filters';
import type { BaserowResponse, BaserowMCQRow } from '../../types/baserow';
import type { MCQ } from '../../types/mcq';

let baserowFields: any[] = [];

export async function initializeBaserow(): Promise<void> {
  try {
    const response = await fetch(
      `${BASEROW_CONFIG.BASE_URL}/fields/table/${BASEROW_CONFIG.TABLE_ID}/`,
      { headers: getBaserowHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch fields: ${response.statusText}`);
    }

    baserowFields = await response.json();
  } catch (error) {
    console.error('Failed to initialize Baserow fields:', error);
    throw error;
  }
}

async function handleBaserowResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Baserow API error (${response.status}): ${errorData.error || response.statusText}`);
  }
  return response.json();
}

async function fetchAllPages(url: URL): Promise<MCQ[]> {
  let allMCQs: MCQ[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const pageUrl = new URL(url.toString());
    const params = buildPaginationParams(page);
    Object.entries(params).forEach(([key, value]) => {
      pageUrl.searchParams.set(key, value);
    });

    const response = await fetch(pageUrl.toString(), {
      headers: getBaserowHeaders(),
    });
    
    const data = await handleBaserowResponse<BaserowResponse<BaserowMCQRow>>(response);
    const mcqs = data.results.map(transformBaserowRow);
    allMCQs = [...allMCQs, ...mcqs];

    hasMore = !!data.next;
    page++;
  }

  return allMCQs;
}

export async function fetchMCQsBySubtopic(subtopic: string): Promise<MCQ[]> {
  if (!BASEROW_CONFIG.TOKEN) {
    throw new Error('Baserow API token is not configured');
  }

  try {
    const url = new URL(`${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/`);
    const filterParams = buildMCQFilter(subtopic);
    
    Object.entries(filterParams).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    return await fetchAllPages(url);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('Error fetching MCQs:', errorMessage);
    throw new Error(`Failed to fetch MCQs: ${errorMessage}`);
  }
}

export async function updateMCQ(mcqId: number, data: Partial<MCQ>): Promise<MCQ> {
  if (!mcqId) {
    throw new Error('MCQ ID is required');
  }

  try {
    // Get the field definitions if not already loaded
    if (baserowFields.length === 0) {
      await initializeBaserow();
    }

    const baserowData = transformToBaserowRow(data);
    
    // Handle Answer field (single select)
    if (data.answer) {
      const answerField = baserowFields.find((f: any) => f.name === 'Answer');
      if (answerField) {
        const option = answerField.select_options.find((opt: any) => opt.value === data.answer);
        if (option) {
          baserowData.Answer = option.id;
        }
      }
    }

    // Handle Topic field (single select)
    if (data.topic) {
      const topicField = baserowFields.find((f: any) => f.name === 'Topic');
      if (topicField) {
        const option = topicField.select_options.find((opt: any) => opt.value === data.topic);
        if (option) {
          baserowData.Topic = option.id;
        }
      }
    }

    // Handle SubTopic field (single select)
    if (data.subtopic) {
      const subtopicField = baserowFields.find((f: any) => f.name === 'SubTopic');
      if (subtopicField) {
        const option = subtopicField.select_options.find((opt: any) => opt.value === data.subtopic);
        if (option) {
          baserowData.SubTopic = option.id;
        }
      }
    }

    // Handle Tags field (multiple select)
    if (data.tags) {
      const tagField = baserowFields.find((f: any) => f.name === 'Tags');
      if (tagField) {
        const tagIds = data.tags
          .map(tag => tagField.select_options.find((opt: any) => opt.value === tag)?.id)
          .filter((id): id is number => id !== undefined);
        baserowData.Tags = tagIds;
      }
    }

    const url = `${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/${mcqId}/?user_field_names=true`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getBaserowHeaders(),
      body: JSON.stringify(baserowData)
    });

    const updatedRow = await handleBaserowResponse<BaserowMCQRow>(response);
    return transformBaserowRow(updatedRow);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to update MCQ: ${errorMessage}`);
  }
}

export async function markAsDuplicate(mcqId: number): Promise<void> {
  if (!mcqId) {
    throw new Error('MCQ ID is required');
  }

  try {
    const url = `${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/${mcqId}/?user_field_names=true`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getBaserowHeaders(),
      body: JSON.stringify({ Duplicate: true })
    });

    await handleBaserowResponse<BaserowMCQRow>(response);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to mark as duplicate: ${errorMessage}`);
  }
}

export async function markAsCompared(mcqId: number): Promise<void> {
  if (!mcqId) {
    throw new Error('MCQ ID is required');
  }

  try {
    const url = `${BASEROW_CONFIG.BASE_URL}/rows/table/${BASEROW_CONFIG.TABLE_ID}/${mcqId}/?user_field_names=true`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getBaserowHeaders(),
      body: JSON.stringify({ Compared: true })
    });

    await handleBaserowResponse<BaserowMCQRow>(response);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to mark as compared: ${errorMessage}`);
  }
}